import Decimal from "decimal.js";
import { latiniseDigits } from "../i18n/format";

/**
 * Normalises whatever the user typed or pasted into a canonical Latin string.
 *
 * "Read Arabic-Indic, write Latin, never the reverse" — Arabic-Indic input is
 * accepted and immediately rewritten to Latin so the field re-renders in Latin
 * digits, which is the one asymmetry the digit invariant allows.
 *
 * The result is a DISPLAY string, not a number: in-progress states like "12."
 * survive intact, because rewriting them mid-edit would move the user's caret
 * and fight their typing.
 */

/** Matches the persisted-amount contract in src/session/schema.ts. */
const MAX_INTEGER_DIGITS = 15;
const MAX_FRACTION_DIGITS = 8;

/**
 * A separator run looks like digit grouping when every group after the first is
 * exactly three digits and the first is one to three. "1.234.567" yes,
 * "1.2.3" no.
 */
function looksLikeGrouping(parts: readonly string[]): boolean {
  if (parts.length < 2) return false;
  const [first, ...rest] = parts;
  if (first === undefined || first.length < 1 || first.length > 3) return false;
  return rest.every((part) => part.length === 3);
}

/** Removes every occurrence of `remove`, and turns `decimal` into ".". */
function assemble(digitsAndSeparators: string, decimalIndex: number): string {
  let out = "";
  for (let i = 0; i < digitsAndSeparators.length; i += 1) {
    const char = digitsAndSeparators[i];
    if (char === undefined) continue;
    if (char === "." || char === ",") {
      if (i === decimalIndex) out += ".";
      // every other separator is grouping and disappears
    } else {
      out += char;
    }
  }
  return out;
}

function clamp(value: string): string {
  const [intPart = "", fracPart] = value.split(".");
  const clampedInt = intPart.slice(0, MAX_INTEGER_DIGITS);
  if (fracPart === undefined) return clampedInt;
  return `${clampedInt}.${fracPart.slice(0, MAX_FRACTION_DIGITS)}`;
}

export function parseAmount(input: string): string {
  if (typeof input !== "string" || input.length === 0) return "";

  // 1. Arabic-Indic and Extended Arabic-Indic digits become Latin; the Arabic
  //    decimal and thousands separators become "." and ",".
  const latin = latiniseDigits(input);

  // 2. Drop everything that cannot be part of a number. This is what removes
  //    leading currency symbols ("$", "﷼", "€") and pasted trailing junk.
  const stripped = latin.replace(/[^0-9.,]/g, "");
  if (stripped.length === 0) return "";

  const dotCount = (stripped.match(/\./g) ?? []).length;
  const commaCount = (stripped.match(/,/g) ?? []).length;
  const lastDot = stripped.lastIndexOf(".");
  const lastComma = stripped.lastIndexOf(",");

  let decimalIndex = -1;

  if (dotCount > 0 && commaCount > 0) {
    // Both conventions present: whichever comes LAST is the decimal separator.
    // "1.234,56" -> comma decimal. "1,234.56" -> dot decimal.
    decimalIndex = Math.max(lastDot, lastComma);
  } else if (dotCount === 1 && commaCount === 0) {
    // A lone dot is our own decimal separator, so prefer that reading.
    // "1.234" is one-point-two-three-four, not one thousand.
    decimalIndex = lastDot;
  } else if (commaCount === 1 && dotCount === 0) {
    // A lone comma is our own GROUPING separator, so prefer that reading when
    // it looks like grouping. "1,234" -> 1234, but "12,5" -> 12.5.
    decimalIndex = looksLikeGrouping(stripped.split(",")) ? -1 : lastComma;
  } else if (dotCount > 1 || commaCount > 1) {
    // Repeated separators are grouping if the groups are all three digits;
    // otherwise treat the last one as the decimal point.
    const separator = dotCount > 1 ? "." : ",";
    const grouping = looksLikeGrouping(stripped.split(separator));
    decimalIndex = grouping ? -1 : stripped.lastIndexOf(separator);
  }

  return clamp(assemble(stripped, decimalIndex));
}

/**
 * The numeric value behind a normalised string. Empty and partial input are
 * ZERO, never NaN — an empty field shows 0 in every row, not blank and not the
 * previous value.
 */
export function amountToDecimal(normalised: string): Decimal {
  if (normalised === "" || normalised === ".") return new Decimal(0);
  try {
    const value = new Decimal(normalised);
    return value.isFinite() ? value : new Decimal(0);
  } catch {
    return new Decimal(0);
  }
}

/** Convenience for callers that only need the number. */
export function parseAmountToDecimal(input: string): Decimal {
  return amountToDecimal(parseAmount(input));
}
